import * as type from '@/utvStore/store/mutation-types'
import { AUTH_API, USER_API, REGISTRATION, SET_ACCESS_TOKEN } from '@/utvStore/store/API'
import { REGISTRATION_API } from '../API'
import store from '@/utvStore/store'
import pushErrorMessage from '@/utils/pushErrorMessage'

const timeoutValue = !!location
  ? /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/.test(location.host) || /^localhost/.test(location.host) ? 20000 : 8000
  : 20000

let axiosConfig = {
  timeout: timeoutValue,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'content-type': 'application/json'
  }
}

const axiosInstance = axios.create(axiosConfig)

const initialState = {
  isLoggedIn: false,
  accessToken: '',
  nonce: '',
  isFesAccount: false,
  unswitched: ''
}

const state = Object.assign({}, initialState)

const getters = {
  isLoggedIn: state => state.isLoggedIn
}

const actions = {
  /**
   * 一時キーを取得
   * @param {Object} data - { email: String, password: String }のログイン情報
   * @return {Promise} resolve時一時キーを返却
   */
  [type.GET_NONCE]({ commit, dispatch }, data) {
    return axiosInstance.post(AUTH_API.login, data).then(
      res => {
        // ID, PWが間違っている場合reject
        if (res.data.response === 'UNMATCHED') {
          Object.assign(res, {
            message: 'メールアドレスかパスワードが間違っています。'
          })
          return Promise.reject(res)
        }

        // 仮登録状態の場合は再送信ページへ遷移
        if (res.data.response === 'UNCONFIRMED') {
          let email = encodeURIComponent(data.email).replace('+', '%2B')
          let password = encodeURIComponent(data.password).replace('+', '%2B')
          Object.assign(res, {
            message: `アドレスの認証が完了しておりません。
            <a href="/user/remainder?email=${email}&password=${password}">こちら</a>
            から再度認証をおこなってください。`
          })
          return Promise.reject(res)
        }
        dispatch(type.LOGOUT).then(() => {
          commit(type.GET_NONCE, res.data.nonce)
          commit(type.GET_SWITCH_STATUS, res.data.unswitched)
        })
      },
      error => {
        error = pushErrorMessage(error)
        return Promise.reject(error)
      }
    )
  },

  /**
   * nonceデータをもとにaccess_tokenを取得する
   * @param {String} nonce -  一時キー
   * @return {Promise} resolve時アクセストークンを返却
   */
  [type.GET_TOKEN_BY_NONCE]({ commit }, nonce) {
    return axiosInstance.post(AUTH_API.getTokenByNonce, { nonce }).then(
      res => {
        if (res.data.response === 'OK') {
          commit(type.SET_ACCESS_TOKEN, res.data.access_token)
        } else {
          return Promise.reject(res)
        }
      },
      error => {
        error = pushErrorMessage(error)
        return Promise.reject(error)
      }
    )
  },

  /**
   * reissue_keyをし
   * @param {String} reissue_key - controller側から返却されるreissue_key
   * @return {Promise} resolve時アクセストークンを返却
   */
  [type.GET_TOKEN_BY_REISSUE_KEY]({ commit }, reissue_key) {
    const url = `${AUTH_API.getTokenByReissuceKey}`
    return axiosInstance.post(url, { reissue_key }).then(
      res => {
        if (res.data.response === 'OK') {
          commit(type.SET_ACCESS_TOKEN, res.data.access_token)
        } else {
          return Promise.reject(res)
        }
      },
      error => {
        error = pushErrorMessage(error)
        return Promise.reject(error)
      }
    )
  },

  /**
   * Laravel側のモジュールにaccess_tokenを渡し、Sessionに保存してもらう
   * @param {Object} data: ログイン画面で渡される{{ $reissue_key }}と
   *                       ログイン処理で取得したアクセストークン。
   *                       { reissue_key: String, access_token: String }の形式
   */
  [type.POST_ACCESS_TOKEN]({ commit }, data) {
    const api = `${AUTH_API.postAccessToken}${data.reissue_key}`
    if (!data.access_token) {
      return Promise.reject('access_tokenが存在しません')
    }
    if (!data.reissue_key) {
      return Promise.reject('reissue_keyが存在しません')
    }

    return axiosInstance.post(api, data).then(
      res => {
        if (res.data.response === 'NG') {
          return Promise.reject(res.data)
        }
      },
      error => {
        error = pushErrorMessage(error)
        return Promise.reject(error)
      }
    )
  },

  // nonce削除
  [type.RESET_NONCE]({ commit }) {
    commit(type.RESET_NONCE)
  },

  // ユーザー仮登録(nonce発行)
  [type.PROVISIONAL_REGISTRATION]({ commit }, data = {}) {
    return axiosInstance.post(REGISTRATION_API.provision, data).then(
      res => {
        const data = {
          nonce: res.data.nonce,
          isFesAccount: res.data.is_fes_account
        }
        commit(type.PROVISIONAL_REGISTRATION, data)
      },
      error => {
        error = pushErrorMessage(error)
        return Promise.reject(error)
      }
    )
  },

  /**
   * ログアウト処理
   * - アクセストークンとユーザー情報を削除
   * - 支払い情報の削除
   * - 一時保存した入力情報を削除
   **/
  [type.LOGOUT]({ commit }, data) {
    return new Promise(resolve => {
      // firebaseのサインアウトを追加
      firebase
        .auth()
        .signOut()
        .then(function() {})
        .catch(function() {})
      commit(type.LOGOUT)
      commit(type.RESET_PAYMENT_INFO)
      localStorage.clear()
      sessionStorage.clear()
      resolve()
    })
  },

  /**
   * FirebaseAuthで取得したトークンでのログイン
   * - トークンでユーザー情報の問い合わせ
   * - 情報が無ければエラーに変更
   **/
  [type.LOGIN_BY_FIREBASE_UID]({ commit }, data) {
    const url = `${AUTH_API.authFirebaseUid}`
    return axiosInstance.post(url, { data }).then(
      res => {
        if (res.data.response != 'OK') {
          Object.assign(res, {
            message: res.data.message
          })
          return Promise.reject(res)
        }
        commit(type.GET_NONCE, res.data.nonce)
      },
      error => {
        error = pushErrorMessage(error)
        return Promise.reject(error)
      }
    )
  },
  /**
   * FirebaseAuthで取得したトークンでの登録
   * - トークンでユーザー情報の問い合わせ
   * - 情報が無ければ新規登録
   **/
  [type.REGISTRATION_BY_FIREBASE_UID]({ commit, dispatch }, data) {
    const url = `${AUTH_API.registrationFirebaseUid}`
    return axiosInstance
      .post(url, { data })
      .then(
        res => {
          // console.log('REGISTRATION_BY_FIREBASE_UID:', res.data, data)
          // 登録済みの場合はエラー
          if (res.data.response != 'OK' && res.data.is_registed) {
            // エラーメッセージはクエリパラメーターにつけるのでこうしています
            return Promise.reject(`error=is_registed&sns=${data.sns}`)
          }
          dispatch(type.LOGOUT).then(() => {
            commit(type.REGISTRATION_BY_FIREBASE_UID, res.data)
          })
        },
        error => {
          return Promise.reject(error)
        }
      )
      .catch(error => {
        return Promise.reject(new Error(error))
      })
  },

  /**
   * FirebaseAuthで取得したトークンで紐付けを行う
   * - トークンでユーザー情報の問い合わせ
   * - 情報が無ければ新規登録
   * - あればエラーを返す
   **/
  [type.RELATION_FIREBASE_UID]({ commit }, data) {
    const url = `${AUTH_API.relationFirebaseUid}`
    return axiosInstance.post(url, { data }).then(
      res => {
        if (res.data.response != 'OK') {
          Object.assign(res, {
            message: res.data.message
          })
          return Promise.reject(res)
        } else {
          Object.assign(res.data, {
            nickname: data.nickname,
            sns: data.sns
          })
          commit(type.REGISTRATION_BY_FIREBASE_UID, res.data)
        }
      },
      error => {
        error = pushErrorMessage(error)
        return Promise.reject(error)
      }
    )
  },
  /**
   * FirebaseAuthで取得したトークンで紐付けを行う
   * - トークンでユーザー情報の問い合わせ
   * - 情報が無ければ新規登録
   * - あればエラーを返す
   **/
  [type.DELETE_FIREBASE_UID]({ commit }, data) {
    const url = `${AUTH_API.deleteFirebaseUid}`
    return axiosInstance.post(url, { data }).then(
      res => {
        if (res.data.response != 'OK') {
          Object.assign(res, {
            message: res.data.message
          })
          return Promise.reject(res)
        }
      },
      error => {
        error = pushErrorMessage(error)
        return Promise.reject(error)
      }
    )
  },

  [type.RESET_REGISTRATION_BY_FIREBASE_UID]({ commit }) {
    commit(type.RESET_REGISTRATION_BY_FIREBASE_UID)
  }
}

const mutations = {
  [type.GET_NONCE](_state, nonce) {
    Object.assign(_state, { nonce })
  },
  [type.GET_SWITCH_STATUS](_state, unswitched) {
    Object.assign(_state, { unswitched })
  },
  [type.GET_TOKEN_BY_NONCE](_state, access_token) {
    Object.assign(_state, { access_token })
  },
  [type.AUTH_SUCCESS](_state, data) {
    Object.assign(_state, {
      isLoggedIn: true
    })
  },
  [type.SET_ACCESS_TOKEN](_state, accessToken) {
    Object.assign(_state, {
      accessToken,
      nonce: '', // アクセストークン取得後は一時キーを削除
      isLoggedIn: true
    })
  },
  [type.LOGOUT](_state, data) {
    Object.assign(_state, initialState)
  },
  [type.RESET_NONCE](_state) {
    Object.assign(_state, { nonce: '' })
  },
  [type.PROVISIONAL_REGISTRATION](_state, data) {
    Object.assign(_state, data)
  },
  [type.LOGIN_BY_FIREBASE_UID](_state, data) {
    Object.assign(_state, data)
  },
  [type.RELATION_FIREBASE_UID](_state, data) {
    Object.assign(_state, data)
  },
  [type.DELETE_FIREBASE_UID](_state, data) {
    Object.assign(_state, data)
  },
  [type.REGISTRATION_BY_FIREBASE_UID](_state, data) {
    Object.assign(_state, {
      nonce: data.nonce
    })
    Object.assign(_state, { data })
  },
  [type.RESET_REGISTRATION_BY_FIREBASE_UID](_state) {
    Object.assign(_state, { data: {} })
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./resources/assets/js/utvStore/store/modules/auth.js